// Overzicht extra werken en aanrekenbare kosten over alle calculaties heen.
// Dient om na de migratie de aanrekenbare kosten na te lopen (SPEC_wijzigingen 4.3) en om per
// oorspronkelijke onderaannemer te zien hoeveel correctiewerk zijn werk veroorzaakte (3.4).
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AANREKENBARE_KOST_CATEGORIEEN, CATEGORIE_LABELS, EXTRA_WERK_CATEGORIEEN } from '@recotex/shared';
import { AppShell, Icon } from '../components/AppShell';
import { EmptyState } from '../components/EmptyState';
import { api, type ExtraRegelFilters } from '../lib/api';
import { fmtEUR, fmtDate } from '../lib/format';

export function ExtraRegelsOverzicht() {
  const [f, setF] = useState<ExtraRegelFilters>({ soort: 'aanrekenbare_kost' });
  const q = useQuery({ queryKey: ['extra-regels', f], queryFn: () => api.extraRegels.list(f) });
  const ploegenQ = useQuery({ queryKey: ['ploegen'], queryFn: () => api.ploegen.list() });
  const ploegen = ploegenQ.data?.ploegen ?? [];
  const regels = q.data?.regels ?? [];
  const categorieen = f.soort === 'extra_werk' ? EXTRA_WERK_CATEGORIEEN : f.soort === 'aanrekenbare_kost' ? AANREKENBARE_KOST_CATEGORIEEN : [...EXTRA_WERK_CATEGORIEEN, ...AANREKENBARE_KOST_CATEGORIEEN];
  const som = regels.reduce((s, r) => s + (r.soort === 'extra_werk' ? r.bedrag : -r.bedrag), 0);
  const projecten = new Set(regels.map((r) => r.calcId)).size;

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div className="max-w-3xl">
          <h1 className="page-title">Extra werken &amp; aanrekenbare kosten</h1>
          <p className="page-subtitle">
            Alle regels over de calculaties heen. Bestaande aanrekenbare kosten werden gemigreerd met "telt mee = ja" op het eigen project — loop
            ze na en pas aan waar nodig.
          </p>
        </div>
        <a className="btn-ghost" href={api.extraRegels.csvUrl(f)} download><Icon name="download" size={18} />CSV</a>
      </div>

      <div className="card-flat mb-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-6 gap-x-4 gap-y-3 items-end">
        <div>
          <label htmlFor="extra-soort" className="field-label">Soort</label>
          <select id="extra-soort" className="select w-full" value={f.soort ?? ''} onChange={(e) => setF({ ...f, soort: e.target.value as ExtraRegelFilters['soort'], categorie: '' })}>
            <option value="">Alle</option>
            <option value="extra_werk">Extra werken</option>
            <option value="aanrekenbare_kost">Aanrekenbare kosten</option>
          </select>
        </div>
        <div>
          <label htmlFor="extra-categorie" className="field-label">Categorie</label>
          <select id="extra-categorie" className="select w-full" value={f.categorie ?? ''} onChange={(e) => setF({ ...f, categorie: e.target.value })}>
            <option value="">Alle</option>
            {categorieen.map((c) => <option key={c} value={c}>{CATEGORIE_LABELS[c] ?? c}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="extra-ploeg" className="field-label">Onderaannemer</label>
          <select id="extra-ploeg" className="select w-full" value={f.ploegId ?? ''} onChange={(e) => setF({ ...f, ploegId: e.target.value })}>
            <option value="">Alle</option>
            {ploegen.map((p) => <option key={p.id} value={p.id}>{p.team}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="extra-oorspronkelijk" className="field-label">Oorspronkelijke onderaannemer</label>
          <select id="extra-oorspronkelijk" className="select w-full" value={f.oorspronkelijkePloegId ?? ''} onChange={(e) => setF({ ...f, oorspronkelijkePloegId: e.target.value })}>
            <option value="">Alle</option>
            {ploegen.map((p) => <option key={p.id} value={p.id}>{p.team}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="extra-toegerekend" className="field-label">Toegerekend aan</label>
          <input id="extra-toegerekend" className="input" placeholder="P-nummer" value={f.toegerekend ?? ''} onChange={(e) => setF({ ...f, toegerekend: e.target.value })} />
        </div>
        <div className="flex flex-col gap-1.5 text-sm">
          <label className="flex items-center gap-2 font-medium"><input type="checkbox" checked={f.anderProject === '1'} onChange={(e) => setF({ ...f, anderProject: e.target.checked ? '1' : '' })} /> Enkel ander project</label>
          <select className="select" value={f.teltMee ?? ''} onChange={(e) => setF({ ...f, teltMee: e.target.value as ExtraRegelFilters['teltMee'] })} aria-label="Telt mee in percentage">
            <option value="">Telt mee: alle</option>
            <option value="ja">Telt mee: ja</option>
            <option value="nee">Telt mee: nee</option>
          </select>
        </div>
      </div>

      {q.isLoading ? <p className="text-secondary">Laden…</p> : regels.length === 0 ? (
        <EmptyState title="Geen regels" description="Geen extra werken of aanrekenbare kosten voor deze filter." />
      ) : (
        <div className="card overflow-x-auto">
          <div className="text-sm text-secondary mb-3">{regels.length} regels op {projecten} calculaties · saldo <strong className="text-primary">{fmtEUR(som)}</strong></div>
          <table className="w-full min-w-[980px]">
            <thead>
              <tr className="thead-row">
                <th className="text-left px-3 py-2">Calculatie</th>
                <th className="text-left px-3 py-2">Categorie</th>
                <th className="text-left px-3 py-2">Omschrijving</th>
                <th className="text-left px-3 py-2">Toegerekend</th>
                <th className="text-left px-3 py-2">Telt mee</th>
                <th className="text-right px-3 py-2">Bedrag</th>
              </tr>
            </thead>
            <tbody>
              {regels.map((r) => (
                <tr key={r.id} className="border-b border-lightgrey last:border-b-0 align-top text-sm">
                  <td className="px-3 py-2">
                    <Link to={`/project/${r.calcId}`} className="font-semibold text-primary underline tabular-nums">{r.calcProjectNummer || '—'}</Link>
                    <div className="text-xs text-secondary">{r.calcKlantNaam} · {r.ploegTeam} · {fmtDate(r.aangemaaktOp)}</div>
                  </td>
                  <td className="px-3 py-2">{r.soort === 'extra_werk' ? 'Extra werk' : 'Kost'}: {CATEGORIE_LABELS[r.categorie] ?? r.categorie}</td>
                  <td className="px-3 py-2">
                    {r.omschrijving}
                    {r.reden && r.reden !== r.omschrijving && <div className="text-xs text-secondary">Reden: {r.reden}</div>}
                    {r.oorspronkelijkePloegTeam && <div className="text-xs text-secondary">Werk van: {r.oorspronkelijkePloegTeam}</div>}
                  </td>
                  <td className="px-3 py-2">{r.toegerekendProject && r.toegerekendProject !== r.calcProjectNummer ? <span className="chip-warning">{r.toegerekendProject}</span> : <span className="text-secondary">eigen project</span>}</td>
                  <td className="px-3 py-2">{r.teltMeeInPercentage ? 'ja' : 'nee'}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-bold ${r.soort === 'extra_werk' ? '' : 'text-danger'}`}>{r.soort === 'extra_werk' ? '' : '−'}{fmtEUR(r.bedrag)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
